import {
  Modal,
  Box,
  SpaceBetween,
  Header,
  Button,
  Spinner,
  Alert,
  CopyToClipboard,
} from "@amzn/awsui-components-console";
import {
  AmortizationTreatment,
  Ard,
  BillingModeType,
  CreateAccountingRegistrationCommand,
  CreateAccountingRegistrationCommandInput,
  EventType,
  FuseAccountingCentralDatastoreClient,
  FusePlan,
  GetRegistrationByAccountingRegistrationIdCommand,
  MigrationTargetStage,
  RefundType,
  Registration,
  TransactionType,
  UpdateAccountingRegistrationCommand,
  UpdatePlanCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import React, { useEffect, useState, useCallback, useContext } from "react";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { InitDomain } from "src/context/domain/domainContext";
import { RegistrationEditorContent } from "./registration-editor-content";

interface AccountingRegistrationsModalProps {
  visible: boolean;
  accountingRegistrationId: string | null;
  onDismiss: () => void;
  mode?: "view" | "create";
  ardForCreate?: Ard;
  allPlans?: FusePlan[];
  onCreateSuccess?: () => void;
}

/**
 * Build a blank registration template pre-filled with values derived from the ARD.
 */
function buildBlankRegistrationTemplate(ard: Ard): Partial<Registration> {
  const description = [ard.buyingCustomerCountryCode, ard.product, ard.term]
    .filter(Boolean)
    .join("-");

  return {
    ardId: ard.id || "",
    clientId: "Fuse",
    description: description,
    marketplace: ard.buyingCustomerCountryCode || "",
    billingMode: ard.billingMode || undefined,
    transactions: [
      {
        transactionType:
          ard.billingMode === BillingModeType.ENTITLEMENT_BILLING
            ? TransactionType.ENTITLEMENT
            : TransactionType.SUBSCRIPTION,
        eventTypes: [EventType.PeriodStart, EventType.Cancel],
        amortizationTreatment: AmortizationTreatment.EVENT_SCHEDULE,
        refundTypes: [RefundType.NO_REFUND, RefundType.PRORATED_REFUND],
        carveSplitName: "",
        lineItems: ["RECEIVABLE", "DEFERRED_REVENUE"],
      },
    ],
    externalDataMapper: "",
    versionMetadata: "",
  };
}

export function AccountingRegistrationModal({
  visible,
  accountingRegistrationId,
  onDismiss,
  mode = "view",
  ardForCreate,
  allPlans,
  onCreateSuccess,
}: AccountingRegistrationsModalProps) {
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<string>("");
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [linkToAllPlans, setLinkToAllPlans] = useState<boolean>(false);

  const { getFacdClient, ardRegionMap, selectedArd, fetchArdData } =
    useJemGen();
  const { authService } = useContext(InitDomain);

  const isCreateMode = mode === "create";

  // Check if user has edit permissions
  const canEdit = authService.ldap.includes("bart-admin");

  /**
   * Resolve the FACD client for the currently selected ARD.
   * Eliminates duplicate domain-lookup + client-creation pattern.
   */
  const resolveFacdClient =
    useCallback(async (): Promise<FuseAccountingCentralDatastoreClient> => {
      if (!selectedArd || !selectedArd.id) {
        throw new Error("No ARD selected");
      }

      const domain = ardRegionMap.get(selectedArd.id);
      if (domain === undefined) {
        throw new Error(`Unknown region for ARD ${selectedArd.id}`);
      }

      const facdClient = await getFacdClient(domain);
      if (!facdClient) {
        throw new Error("No FACD client found for region");
      }

      return facdClient;
    }, [selectedArd, ardRegionMap, getFacdClient]);

  const getRegistrationByAccountingRegistrationId = useCallback(
    async (accountingRegistrationId: string) => {
      try {
        const command = new GetRegistrationByAccountingRegistrationIdCommand({
          accountingRegistrationId,
        });

        console.log(
          "Starting FACD getRegistrationByAccountingRegistrationId operation:",
          command,
        );

        const facdClient = await resolveFacdClient();
        const response = await facdClient.send(command);

        if (response && response.registration) {
          return response.registration;
        }
      } catch (error: unknown) {
        console.error(
          "An error occurred in getRegistrationByAccountingRegistrationId:",
          error,
        );
        throw error;
      } finally {
        console.log(
          "GetRegistrationByAccountingRegistrationId operation completed.",
        );
      }
    },
    [resolveFacdClient],
  );

  const fetchRegistration = useCallback(async () => {
    if (!accountingRegistrationId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getRegistrationByAccountingRegistrationId(
        accountingRegistrationId,
      );
      if (result) {
        setRegistration(result);
      } else {
        setError("Registration not found");
      }
    } catch (error: unknown) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to fetch registration details",
      );
    } finally {
      setLoading(false);
    }
  }, [accountingRegistrationId, getRegistrationByAccountingRegistrationId]);

  const updateAccountingRegistration = useCallback(
    async (newRegistrationJsonString: string) => {
      setIsUpdating(true);
      setUpdateError(null);
      setUpdateSuccess(false);

      try {
        // Parse and validate JSON
        let newRegistration: Registration;
        try {
          newRegistration = JSON.parse(newRegistrationJsonString);
        } catch (parseError) {
          throw new Error("Invalid JSON format");
        }

        // Ensure accountingRegistrationId hasn't changed
        if (
          registration &&
          newRegistration.accountingRegistrationId !==
            registration.accountingRegistrationId
        ) {
          throw new Error(
            "Cannot modify accountingRegistrationId field. This field is read-only.",
          );
        }

        const facdClient = await resolveFacdClient();

        // Send update command
        const updateAccountingRegistrationCommand =
          new UpdateAccountingRegistrationCommand(newRegistration);
        console.log(
          "Starting FACD updateAccountingRegistration operation:",
          updateAccountingRegistrationCommand,
        );

        await facdClient.send(updateAccountingRegistrationCommand);

        console.log(
          "UpdateAccountingRegistration operation completed successfully.",
        );

        // Success: show success message and refresh data
        setUpdateSuccess(true);
        setIsEditMode(false);
        setEditedContent("");

        // Refresh the registration data
        await fetchRegistration();
      } catch (error: unknown) {
        console.error(
          "An error occurred in updateAccountingRegistration:",
          error,
        );
        setUpdateError(
          error instanceof Error
            ? error.message
            : "Failed to update registration",
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [registration, resolveFacdClient, fetchRegistration],
  );

  /**
   * Link a registration to all plans in the ARD by calling UpdatePlanCommand for each.
   */
  const linkRegistrationToAllPlans = useCallback(
    async (registrationId: string) => {
      if (!allPlans || allPlans.length === 0) return;

      const facdClient = await resolveFacdClient();

      const plansToLink = allPlans.filter(
        (plan) => plan.id && plan.accountingRegistrationId !== registrationId,
      );

      const results = await Promise.allSettled(
        plansToLink.map(async (plan) => {
          const updatePlanCommand = new UpdatePlanCommand({
            id: plan.id!,
            ardId: plan.ardId || selectedArd!.id,
            glLineDescriptionPartnerName: plan.glLineDescriptionPartnerName!,
            fusenEnabled: plan.fusenEnabled ?? false,
            migrationTargetStage:
              plan.migrationTargetStage! as MigrationTargetStage,
            fusenEnableDate: plan.fusenEnableDate || "",
            accountingRegistrationId: registrationId,
            version: plan.version,
          });

          console.log(
            `Linking registration ${registrationId} to plan ${plan.id}`,
          );
          await facdClient.send(updatePlanCommand);
          console.log(`Successfully linked registration to plan ${plan.id}`);
        }),
      );

      const failedIds = plansToLink
        .filter((_, i) => results[i].status === "rejected")
        .map((p) => p.id);

      if (failedIds.length > 0) {
        throw new Error(
          `Failed to link ${failedIds.length} plan(s): ${failedIds.join(", ")}`,
        );
      }
    },
    [allPlans, selectedArd, resolveFacdClient],
  );

  /**
   * Create a new accounting registration using CreateAccountingRegistrationCommand.
   */
  const createAccountingRegistration = useCallback(
    async (registrationJsonString: string) => {
      setIsUpdating(true);
      setUpdateError(null);
      setUpdateSuccess(false);

      try {
        // Parse and validate JSON
        let registrationData: CreateAccountingRegistrationCommandInput;
        try {
          registrationData = JSON.parse(registrationJsonString);
        } catch (parseError) {
          throw new Error("Invalid JSON format");
        }

        const facdClient = await resolveFacdClient();

        // Send create command
        const createCommand = new CreateAccountingRegistrationCommand(
          registrationData,
        );

        console.log(
          "Starting FACD createAccountingRegistration operation:",
          createCommand,
        );

        const response = await facdClient.send(createCommand);

        console.log(
          "CreateAccountingRegistration operation completed successfully.",
          response,
        );

        const newRegistrationId =
          response?.registration?.accountingRegistrationId;

        // Handle linking separately to avoid misleading error messages
        if (linkToAllPlans && newRegistrationId) {
          try {
            console.log("Linking new registration to all plans in ARD...");
            await linkRegistrationToAllPlans(newRegistrationId);
          } catch (linkError) {
            console.error("Failed to link registration to plans:", linkError);
            // Partial success: registration created, linking failed
            setUpdateSuccess(true);
            setUpdateError(
              `Registration created successfully, but failed to link to plans: ${
                linkError instanceof Error ? linkError.message : "Unknown error"
              }`,
            );
            // Still refresh data since registration was created
            if (selectedArd?.id) await fetchArdData(selectedArd.id);
            if (onCreateSuccess) onCreateSuccess();

            return;
          }
        }

        setUpdateSuccess(true);

        // Reload ARD data so the registration shows up
        if (selectedArd?.id) {
          await fetchArdData(selectedArd.id);
        }

        // Call success callback
        if (onCreateSuccess) {
          onCreateSuccess();
        }

        // Close the modal after successful creation
        onDismiss();
      } catch (error: unknown) {
        console.error(
          "An error occurred in createAccountingRegistration:",
          error,
        );
        setUpdateError(
          error instanceof Error
            ? error.message
            : "Failed to create registration",
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [
      selectedArd,
      resolveFacdClient,
      linkToAllPlans,
      linkRegistrationToAllPlans,
      fetchArdData,
      onCreateSuccess,
      onDismiss,
    ],
  );

  useEffect(() => {
    if (visible && isCreateMode && ardForCreate) {
      if (!canEdit) {
        // User doesn't have permission to create
        setError(
          "You do not have permission to create accounting registrations. The bart-admin LDAP group is required.",
        );
        setIsEditMode(false);
        setRegistration(null);
        setEditedContent("");
        setUpdateError(null);
        setUpdateSuccess(false);
        setLinkToAllPlans(false);
      } else {
        // Create mode: start with edit mode and blank template
        const template = buildBlankRegistrationTemplate(ardForCreate);
        setEditedContent(JSON.stringify(template, null, 2));
        setIsEditMode(true);
        setRegistration(null);
        setError(null);
        setUpdateError(null);
        setUpdateSuccess(false);
        setLinkToAllPlans(false);
      }
    } else if (
      visible &&
      !isCreateMode &&
      accountingRegistrationId &&
      selectedArd
    ) {
      // View mode: fetch existing registration
      fetchRegistration();
    } else if (!visible) {
      // Reset states when modal is closed
      setIsEditMode(false);
      setEditedContent("");
      setUpdateError(null);
      setUpdateSuccess(false);
      setLinkToAllPlans(false);
      setRegistration(null);
      setError(null);
    }
  }, [
    visible,
    isCreateMode,
    ardForCreate,
    accountingRegistrationId,
    selectedArd,
    fetchRegistration,
    canEdit,
  ]);

  const handleSaveOrCreate = () => {
    if (isCreateMode) {
      createAccountingRegistration(editedContent);
    } else {
      updateAccountingRegistration(editedContent);
    }
  };

  const modalTitle = isCreateMode
    ? "Create Accounting Registration"
    : "Accounting Registration Details";

  const saveButtonText = isCreateMode ? "Create" : "Save";

  return (
    <Modal
      size="large"
      visible={visible}
      onDismiss={onDismiss}
      closeAriaLabel="Close modal"
      header={
        <Header variant="h2">
          {modalTitle}
          {!isCreateMode && accountingRegistrationId && (
            <span
              style={{
                fontSize: "14px",
                fontWeight: "normal",
                marginLeft: "10px",
              }}
            >
              ({accountingRegistrationId})
            </span>
          )}
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {!isEditMode && !isCreateMode && registration && (
              <CopyToClipboard
                copyButtonText="Copy JSON"
                copyErrorText="Failed to copy JSON"
                copySuccessText="Registration JSON copied"
                textToCopy={JSON.stringify(registration, null, 2)}
                variant="button"
              />
            )}
            {!isEditMode && !isCreateMode && canEdit && registration && (
              <Button
                onClick={() => {
                  setIsEditMode(true);
                  setEditedContent(JSON.stringify(registration, null, 2));
                  setUpdateError(null);
                  setUpdateSuccess(false);
                }}
              >
                Edit
              </Button>
            )}
            {isEditMode && (
              <>
                {!isCreateMode && (
                  <Button
                    onClick={() => {
                      setIsEditMode(false);
                      setEditedContent("");
                      setUpdateError(null);
                    }}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSaveOrCreate}
                  variant="primary"
                  disabled={isUpdating}
                  loading={isUpdating}
                >
                  {saveButtonText}
                </Button>
              </>
            )}
            <Button
              onClick={onDismiss}
              variant={isEditMode || isCreateMode ? "normal" : "primary"}
            >
              {isCreateMode ? "Cancel" : "Close"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {loading && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box margin={{ top: "s" }}>Loading registration details...</Box>
          </Box>
        )}

        {error && !loading && (
          <Alert type="error" header="Error loading registration">
            {error}
          </Alert>
        )}

        {updateSuccess && (
          <Alert
            type="success"
            dismissible
            onDismiss={() => setUpdateSuccess(false)}
            header={isCreateMode ? "Creation successful" : "Update successful"}
          >
            {isCreateMode
              ? "Registration created successfully"
              : "Registration updated successfully"}
          </Alert>
        )}

        {updateError && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setUpdateError(null)}
            header={isCreateMode ? "Creation failed" : "Update failed"}
          >
            {updateError}
          </Alert>
        )}

        <RegistrationEditorContent
          isCreateMode={isCreateMode}
          isEditMode={isEditMode}
          registration={registration}
          loading={loading}
          error={error}
          editedContent={editedContent}
          isUpdating={isUpdating}
          linkToAllPlans={linkToAllPlans}
          onEditorChange={setEditedContent}
          onLinkToAllPlansChange={setLinkToAllPlans}
        />
      </SpaceBetween>
    </Modal>
  );
}
